import * as React from "react";
import styled, { css } from "styled-components";

import { createEntitiesConsumer } from "../../lib/entities";
import { Image } from "../../entities";
import { PortfolioProject } from "../entities";
import { colors, media, truncateLines } from "../../common/utils/style";
import Img from "../../common/components/Image";
import Hover from "../../common/components/Hover";

interface Props {
  project: PortfolioProject;
  floatsByHover?: boolean;
}

const ImageEntitiesConsumer = createEntitiesConsumer<{ image: Image }>({ image: "images" });

const PortfolioProjectCard: React.SFC<Props> = props => {
  const { project, floatsByHover } = props;
  return (
    <Hover>
      {({ isHovered, ref }) => (
        <Base
          innerRef={ref}
          isHovered={Boolean(floatsByHover) && isHovered}
          href={project.canonical_url}
          target="_blank"
        >
          <CoverImageWrapper>
            <ImageEntitiesConsumer query={{ image: project.cover_image }}>
              {({ image }) => image && <CoverImage image={image} alt={project.title} size="330x190#" background />}
            </ImageEntitiesConsumer>
          </CoverImageWrapper>
          <Detail>
            <Title>{project.title}</Title>
            <Description>{project.description}</Description>
          </Detail>
        </Base>
      )}
    </Hover>
  );
};

interface StyleProps {
  isHovered: boolean;
}

const Base = styled.a`
  display: flex;
  flex-direction: column;
  margin: 10px 0 30px;
  border-radius: 10px;
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.08);
  overflow: hidden;
  color: inherit;
  &:hover {
    text-decoration: none;
  }
  &:last-child {
    margin-right: 0;
  }
  @media ${media.large} {
    flex-direction: row-reverse;
    border-radius: 6px;
    border: solid 1px ${colors.lightergray1};
    box-shadow: none;
    transform: translateY(0);
    transition: transform 200ms ease-out, box-shadow 200ms ease-out;
    ${(props: StyleProps) =>
      props.isHovered
        ? css`
            transform: translateY(-7px);
            box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.1);
          `
        : css``};
  }
`;

const Detail = styled.div`
  height: 90px;
  padding: 14px 14px;
  box-sizing: border-box;
  @media ${media.large} {
    flex-grow: 1;
    height: auto;
    padding: 24px 32px 24px 24px;
  }
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 7px;
  color: ${colors.gray4};
  line-height: 1.2;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  @media ${media.large} {
    overflow: unset;
    white-space: unset;
    text-overflow: unset;
    font-size: 20px;
    margin-bottom: 14px;
  }
`;

const Description = styled.div`
  font-size: 13px;
  color: ${colors.gray2};
  ${truncateLines(2, 12, 1.6)};
  @media ${media.large} {
    ${truncateLines(8, 13, 1.8)};
  }
`;

const CoverImageWrapper = styled.div`
  width: 100%;
  height: 190px;
  background-color: ${colors.lightergray2};
  overflow: hidden;
  @media ${media.large} {
    width: 284px;
    height: 345px;
    flex-shrink: 0;
  }
`;

const CoverImage = styled(Img)`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
`;

export default PortfolioProjectCard;



// WEBPACK FOOTER //
// ./frontend/src/portfolio_projects/components/PortfolioProjectCard.tsx